import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { IEmailTemplate } from '../../../models/email-template.model';
import { EmailTemplatesService } from '../../../services/email-templates.service';
import { SidenavService } from '../../../services/sidenav.service';
import { SnackbarService } from '../../../services/snackbar.service';

@Component({
  selector: 'app-email-template-edit',
  templateUrl: './email-template-edit.component.html',
  styleUrls: ['./email-template-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EmailTemplateEditComponent implements OnInit {
  @BlockUI() blockUI!: NgBlockUI;

  sidenav: MatSidenav;
  isNew = false;
  template: IEmailTemplate = this.emptyTemplate();

  newVariable = '';
  previewHtml = '';
  previewSubject = '';
  showTestSendDialog = false;
  testSendEmail = '';

  tinymceConfig: Record<string, any> = {
    height: 520,
    menubar: false,
    plugins: ['code'],
    toolbar: 'bold | code',
    content_style: 'body { font-family: sans-serif; font-size: 14px; }',
    promotion: false,
    branding: false,
  };

  constructor(
    private sidenavService: SidenavService,
    private emailTemplatesService: EmailTemplatesService,
    private snackbar: SnackbarService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
  ) {
    this.sidenav = this.sidenavService.getSidenav();
  }

  async ngOnInit(): Promise<void> {
    const key = this.route.snapshot.paramMap.get('key');
    if (!key || key === 'new') {
      this.isNew = true;
      return;
    }

    this.blockUI.start();
    try {
      this.template = await this.emailTemplatesService.getByKey(key);
    } catch {
      this.snackbar.error('No se ha podido cargar la plantilla.');
      this.router.navigate(['/admin/email-templates']);
    } finally {
      this.blockUI.stop();
      this.cdr.markForCheck();
    }
  }

  toggleMenu(): void {
    this.sidenavService.toggle();
  }

  goBack(): void {
    this.router.navigate(['/admin/email-templates']);
  }

  async save(form: NgForm): Promise<void> {
    if (form.invalid) return;

    this.blockUI.start();
    try {
      if (this.isNew) {
        await this.emailTemplatesService.create(this.template);
        this.snackbar.success('Plantilla creada correctamente.');
      } else {
        await this.emailTemplatesService.update(this.template);
        this.snackbar.success('Plantilla guardada correctamente.');
      }
      this.router.navigate(['/admin/email-templates']);
    } catch {
      this.snackbar.error('Error al guardar la plantilla.');
    } finally {
      this.blockUI.stop();
      this.cdr.markForCheck();
    }
  }

  addVariable(): void {
    const v = this.newVariable.trim();
    if (!v) return;
    if (!this.template.allowedVariables.includes(v)) {
      this.template.allowedVariables = [...this.template.allowedVariables, v];
    }
    this.newVariable = '';
    this.cdr.markForCheck();
  }

  removeVariable(index: number): void {
    this.template.allowedVariables = this.template.allowedVariables.filter(
      (_, i) => i !== index,
    );
    this.cdr.markForCheck();
  }

  setRecipients(field: 'to' | 'cc' | 'bcc', value: string): void {
    const emails = value
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);
    this.template.recipients = { ...this.template.recipients, [field]: emails };
  }

  async previewTemplate(): Promise<void> {
    this.blockUI.start();
    try {
      const result = await this.emailTemplatesService.preview(
        this.template.key,
        {},
      );
      this.previewHtml = result.html;
      this.previewSubject = result.subject;
      this.cdr.markForCheck();
    } catch {
      this.snackbar.error('No se ha podido generar la vista previa.');
    } finally {
      this.blockUI.stop();
    }
  }

  closePreview(): void {
    this.previewHtml = '';
    this.previewSubject = '';
    this.cdr.markForCheck();
  }

  openTestSendDialog(): void {
    this.showTestSendDialog = true;
    this.cdr.markForCheck();
  }

  async sendTestEmail(): Promise<void> {
    if (!this.testSendEmail) return;
    this.blockUI.start();
    try {
      await this.emailTemplatesService.testSend(
        this.template.key,
        this.testSendEmail,
        {},
      );
      this.snackbar.success('Email de prueba enviado.');
      this.showTestSendDialog = false;
    } catch {
      this.snackbar.error('Error al enviar el email de prueba.');
    } finally {
      this.blockUI.stop();
      this.cdr.markForCheck();
    }
  }

  async resetDefaults(): Promise<void> {
    this.blockUI.start();
    try {
      this.template = await this.emailTemplatesService.resetDefaults(
        this.template.key,
      );
      this.snackbar.success('Plantilla restaurada a valores por defecto.');
      this.cdr.markForCheck();
    } catch {
      this.snackbar.error('No se ha podido restaurar la plantilla.');
    } finally {
      this.blockUI.stop();
    }
  }

  private emptyTemplate(): IEmailTemplate {
    return {
      key: '',
      name: '',
      description: '',
      category: '',
      subjectTemplate: '',
      htmlTemplate: '',
      textTemplate: '',
      enabled: true,
      allowedVariables: [],
      recipients: { to: [], cc: [], bcc: [] },
      version: 1,
    };
  }
}
